import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Moon,
  ListTree,
  Users,
  PhoneForwarded,
  Music2,
  Mail,
  Eye,
  ArrowLeftRight,
  PhoneOff,
  ArrowRight,
  Hash,
  CheckCircle,
  Calendar,
  Settings,
  ShieldCheck } from
"lucide-react";

const flipCards = [
{
  icon: Moon,
  name: "Nachtschakeling",
  color: "bg-hero-gradient",
  back: "Buiten werktijd automatisch andere regels. Bellers horen een passend bericht of worden doorgestuurd — jij hoeft er niets voor te doen."
},
{
  icon: ListTree,
  name: "Keuzemenu (IVR)",
  color: "bg-rinqel-blue-gradient",
  back: "\"Toets 1 voor verkoop…\" Professioneel en duidelijk. Bellers komen direct bij de juiste afdeling terecht, zonder zoeken."
},
{
  icon: Users,
  name: "Belgroepen",
  color: "bg-rinqel-green-gradient",
  back: "Laat gesprekken tegelijk of in volgorde rinkelen bij je team. Niemand wordt gemist, iedereen kan helpen."
},
{
  icon: PhoneForwarded,
  name: "Doorschakelen",
  color: "bg-hero-gradient",
  back: "Oproepen volgen jou — naar mobiel, thuis of een collega. Altijd bereikbaar, ongeacht waar je bent."
},
{
  icon: Music2,
  name: "Wachtrij & wachtmuziek",
  color: "bg-rinqel-blue-gradient",
  back: "Houd bellers professioneel in de lijn met muziek of een bericht. Geeft vertrouwen en vermindert ophangmomenten."
},
{
  icon: Mail,
  name: "Voicemail naar e‑mail",
  color: "bg-rinqel-green-gradient",
  back: "Gemiste berichten komen direct als audiobestand in je inbox. Handig voor teams die altijd up-to-date willen zijn."
},
{
  icon: Eye,
  name: "Status & bereikbaarheid",
  color: "bg-hero-gradient",
  back: "Zie direct wie er beschikbaar is. Collega's nemen makkelijk gesprekken over zonder dat bellers het merken."
},
{
  icon: ArrowLeftRight,
  name: "Doorverbinden & wisselgesprek",
  color: "bg-rinqel-blue-gradient",
  back: "Verbind bellers intern door of neem twee gesprekken aan tegelijk — zoals in een groot bedrijf, maar zonder dure centrale."
},
{
  icon: PhoneOff,
  name: "Niet storen",
  color: "bg-rinqel-green-gradient",
  back: "Even ongestoord werken? Zet je beschikbaarheid op 'niet storen'. Gesprekken gaan netjes naar een collega of voicemail."
}];


const benefits = [
{
  icon: Calendar,
  title: "Maandelijks opzegbaar",
  desc: "Geen langlopende contracten. Je zit nooit vast.",
  color: "bg-hero-gradient"
},
{
  icon: Settings,
  title: "Dagelijks aanpasbaar",
  desc: "Voeg gebruikers toe of pas instellingen aan — direct, zonder extra kosten.",
  color: "bg-rinqel-blue-gradient"
},
{
  icon: ShieldCheck,
  title: "99%+ uptime",
  desc: "Drie datacenters als basis. Jouw bereikbaarheid is in veilige handen.",
  color: "bg-rinqel-green-gradient"
}];


const TelefonieContent = () => {
  return (
    <>
      {/* Intro with colored accent bar */}
      <motion.div
        className="mt-16 mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}>

        <div className="flex items-center gap-3 mb-4">
          <span className="h-1 w-10 rounded-full bg-hero-gradient" />
          <span className="text-xs font-semibold uppercase tracking-widest text-primary">Inbegrepen functies</span>
        </div>
        <h2 className="text-2xl font-bold md:text-3xl max-w-3xl">
          Slim geregeld,{" "}
          <span className="text-gradient">zonder gedoe</span>
        </h2>
        <p className="mt-4 text-lg leading-relaxed text-muted-foreground max-w-3xl">
          Rinqel telefonie is meer dan alleen bellen. Het is een compleet communicatieplatform dat je team professioneler
          laat overkomen — of je nu op kantoor zit, thuis werkt of onderweg bent. Alle functies zijn inbegrepen, zonder
          verstopte kosten.
        </p>
      </motion.div>

      {/* Flip cards */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {flipCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <div
              key={card.name}
              className="flip-card h-48">

              <div className="flip-card-inner">
                {/* Front */}
                <div className="flip-card-front flex flex-col items-start justify-between rounded-xl border border-border bg-card p-6 shadow-card">
                  <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${card.color}`}>
                    <Icon className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <span className="text-base font-semibold">{card.name}</span>
                    <p className="mt-1 text-xs text-muted-foreground">Hover voor voordeel →</p>
                  </div>
                </div>

                {/* Back */}
                <div className={`flip-card-back flex flex-col items-start justify-between rounded-xl p-6 shadow-card ${card.color}`}>
                  <CheckCircle className="h-6 w-6 text-white/90 shrink-0" />
                  <p className="text-sm leading-relaxed text-white font-medium">{card.back}</p>
                </div>
              </div>
            </div>);

        })}
      </div>

      {/* Sub-page teasers */}
      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {/* Functies teaser */}
        <motion.div
          className="relative overflow-hidden flex flex-col justify-between rounded-2xl bg-rinqel-blue-gradient p-8 shadow-card"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}>

          {/* Decorative circle */}
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10" />
          <div className="absolute -right-2 top-16 h-20 w-20 rounded-full bg-white/5" />

          <div className="relative">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
              <Settings className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">Alle functies uitgelegd</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/80">
              Nachtschakeling, conferentiegesprek, nummerweergave en meer — we leggen elk onderdeel stap voor stap uit
              zodat je precies weet wat je krijgt.
            </p>
          </div>
          <Button
            className="mt-6 self-start bg-white text-secondary hover:bg-white/90 font-semibold"
            asChild>

            <Link to="/diensten/telefonie/functies">
              Bekijk alle functies <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </motion.div>

        {/* Nummers teaser */}
        <motion.div
          className="relative overflow-hidden flex flex-col justify-between rounded-2xl bg-hero-gradient p-8 shadow-card"
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}>

          {/* Decorative circle */}
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10" />
          <div className="absolute -right-2 top-16 h-20 w-20 rounded-full bg-white/5" />

          <div className="relative">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
              <Hash className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">Nummers voor elke situatie</h3>
            <p className="mt-2 text-sm leading-relaxed text-white/80">
              Regionaal, 085, 088, 0800/0900 of internationaal — volledig prijsoverzicht met aansluitkosten en blokgrootten.{" "}
              <span className="font-bold text-white">Vanaf € 2,50 p/m.</span>
            </p>
          </div>
          <Button
            className="mt-6 self-start bg-white text-primary hover:bg-white/90 font-semibold"
            asChild>

            <Link to="/diensten/telefonie/nummers">
              Bekijk alle nummers <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </motion.div>
      </div>

      {/* Gesprekskosten teaser — full-width banner */}
      <div className="mt-6 relative overflow-hidden rounded-2xl bg-rinqel-blue-gradient p-8 md:p-10 shadow-card">
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
        <div className="absolute -left-4 bottom-0 h-28 w-28 rounded-full bg-white/5" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-3 py-1 text-xs font-semibold text-white mb-4">
              <span className="h-1.5 w-1.5 rounded-full bg-white" />
              Gesprekskosten
            </span>
            <h3 className="text-xl font-bold text-white md:text-2xl">
              Wat kost een gesprek precies?
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-white/80">
              Per seconde afgerekend, intern bellen altijd gratis, en 0800-nummers kosten je nooit iets. Bekijk het volledige tariefoverzicht voor nationaal én internationaal.
            </p>
            <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/90">
              {["Per seconde", "Intern gratis", "0800 gratis"].map((item) =>
              <li key={item} className="flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4 text-white/70 shrink-0" />
                  {item}
                </li>
              )}
            </ul>
          </div>
          <Button
            className="shrink-0 bg-white text-secondary hover:bg-white/90 font-semibold"
            size="lg"
            asChild>

            <Link to="/diensten/telefonie/gesprekskosten">
              Bekijk alle tarieven <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>

      {/* Overstappen teaser — full-width banner */}
      <div className="mt-8 relative overflow-hidden rounded-2xl bg-rinqel-green-gradient p-8 md:p-10 shadow-card">
        {/* Decorative circles */}
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
        <div className="absolute -left-4 bottom-0 h-28 w-28 rounded-full bg-white/5" />

        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-3 py-1 text-xs font-semibold text-white mb-4">
              <span className="h-1.5 w-1.5 rounded-full bg-white" />
              Overstapdienst
            </span>
            <h3 className="text-xl font-bold text-white md:text-2xl">
              Overstappen?
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-white/80">
              Wij regelen de hele overstap voor je — inclusief je bestaande nummers. Geen gedoe, geen onderbrekingen. Gemiddeld binnen 1 à 2 weken klaar.
            </p>
            <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/90">
              {["Nummers meenemen", "Gratis adviesgesprek", "Maandelijks opzegbaar"].map((item) =>
              <li key={item} className="flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4 text-white/70 shrink-0" />
                  {item}
                </li>
              )}
            </ul>
          </div>
          <Button
            className="shrink-0 bg-white text-secondary hover:bg-white/90 font-semibold"
            size="lg"
            asChild>

            <Link to="/diensten/telefonie/overstappen">
              Hoe werkt overstappen? <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>


      <div className="mt-14 grid gap-4 sm:grid-cols-3">
        {benefits.map((b, i) => {
          const Icon = b.icon;
          return (
            <motion.div
              key={b.title}
              className={`relative overflow-hidden flex flex-col gap-3 rounded-2xl p-6 ${b.color} shadow-card`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}>

              <div className="absolute -right-4 -bottom-4 h-24 w-24 rounded-full bg-white/10" />
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20">
                <Icon className="h-5 w-5 text-white" />
              </div>
              <div className="relative">
                <p className="font-bold text-white">{b.title}</p>
                <p className="mt-1 text-sm text-white/80">{b.desc}</p>
              </div>
            </motion.div>);

        })}
      </div>

      {/* CTA */}
      <motion.div
        className="mt-14 rounded-2xl border border-border bg-hero-gradient-subtle p-10 text-center"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}>

        <h3 className="text-2xl font-bold">Klaar om professioneler te bellen?</h3>
        <p className="mt-2 text-muted-foreground">Maandelijks opzegbaar, dagelijks aanpasbaar. Geen gedoe.</p>
        <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <Button variant="hero" size="lg" asChild>
            <Link to="/configurator">Bereken je prijs <ArrowRight className="h-4 w-4" /></Link>
          </Button>
          <Button variant="hero-outline" size="lg" asChild>
            <Link to="/contact">Neem contact op</Link>
          </Button>
        </div>
      </motion.div>
    </>);

};

export default TelefonieContent;